import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import {
  credentialsApi,
  type CredentialSummary,
} from '@/api/credentials';
import { errorMessage } from '@/api/client';
import {
  decryptCredential,
  encryptCredential,
  type CredentialPayload,
} from '@/crypto/sodium';
import {
  computeTotp,
  isValidBase32,
  parseOtpauthUrl,
  TOTP_DEFAULTS,
  totpMsUntilNext,
  type TotpAlgorithm,
} from '@/crypto/totp';
import { useAuthStore } from '@/stores/auth';
import { copySecret } from '@/lib/clipboard';
import i18n from '@/i18n';
import { alertDialog, confirmDialog } from '@/stores/dialog';

interface TotpSeed {
  id: string;
  title: string;
  issuer: string | null;
  account: string | null;
  secret: string;
  algorithm: TotpAlgorithm;
  digits: number;
  period: number;
}

// ============================================================
// Page
// ============================================================

export default function AuthenticatorPage() {
  const { t } = useTranslation();
  const tenantId = useAuthStore((s) => s.currentTenantId);
  const keyPair = useAuthStore((s) => s.keyPair);
  const queryClient = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [seeds, setSeeds] = useState<TotpSeed[] | null>(null);

  const list = useQuery({
    queryKey: ['credentials', tenantId],
    queryFn: () => credentialsApi.list(tenantId!),
    enabled: !!tenantId,
  });

  const totpSummaries = useMemo(
    () => (list.data ?? []).filter((c) => c.type === 'totp_seed'),
    [list.data],
  );

  useEffect(() => {
    if (!keyPair || !totpSummaries.length) {
      setSeeds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadError(null);
      try {
        const results: TotpSeed[] = [];
        for (const s of totpSummaries) {
          // Pagina generează coduri la fiecare vizită — nu e o „accesare" de numărat.
          const detail = await credentialsApi.get(s.id, { noTrack: true });
          const payload = (await decryptCredential(
            {
              ciphertext: detail.ciphertext,
              nonce: detail.nonce,
              dek_wrapped: detail.dek_wrapped,
            },
            keyPair,
          )) as CredentialPayload;
          const secret = typeof payload.secret === 'string' ? payload.secret : '';
          if (!secret || !isValidBase32(secret)) continue;
          results.push({
            id: s.id,
            title: s.title,
            issuer: metaString(s, 'issuer'),
            account: metaString(s, 'account'),
            secret,
            algorithm:
              (typeof payload.algorithm === 'string' &&
                ['SHA1', 'SHA256', 'SHA512'].includes(payload.algorithm)
                ? (payload.algorithm as TotpAlgorithm)
                : TOTP_DEFAULTS.algorithm),
            digits: numOr(payload.digits, TOTP_DEFAULTS.digits),
            period: numOr(payload.period, TOTP_DEFAULTS.period),
          });
        }
        if (!cancelled) setSeeds(results);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : i18n.t('admin_ui.authenticator.decrypt_error'));
          setSeeds([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [totpSummaries, keyPair]);

  if (!tenantId || !keyPair) {
    return <p className="text-sm text-[var(--text-muted)]">{t('vault.no_tenant')}</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="display-title">{t('authenticator.title')}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {t('authenticator.subtitle')}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAddOpen(true)}>
          {t('authenticator.add')}
        </button>
      </div>

      {addOpen && (
        <AddTotpCard
          tenantId={tenantId}
          ownerPublicKey={keyPair.publicKey}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            queryClient.invalidateQueries({ queryKey: ['credentials', tenantId] });
          }}
        />
      )}

      {list.isLoading && (
        <p className="text-sm text-[var(--text-muted)]">{t('common.loading')}</p>
      )}

      {loadError && (
        <div
          className="text-sm rounded px-3 py-2 mb-4"
          style={{
            color: 'var(--danger)',
            background: 'var(--danger-bg)',
            border: '1px solid var(--danger)',
          }}
        >
          {t('admin_ui.authenticator.decrypt_error')}: {loadError}
        </div>
      )}

      {seeds && seeds.length === 0 && !list.isLoading && (
        <div className="card text-center py-12">
          <p className="text-sm text-[var(--text-muted)]">
            <Trans i18nKey="admin_ui.authenticator.empty" components={{ strong: <strong /> }} />
          </p>
        </div>
      )}

      {seeds && seeds.length > 0 && (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {seeds.map((seed) => (
            <TotpCard
              key={seed.id}
              seed={seed}
              onDelete={() =>
                queryClient.invalidateQueries({ queryKey: ['credentials', tenantId] })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TotpCard — afișează cod rotativ + progress + copy
// ============================================================

function TotpCard({ seed, onDelete }: { seed: TotpSeed; onDelete: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState<string>('—');
  const [progress, setProgress] = useState(100);
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const feedbackTimer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      try {
        const next = await computeTotp({
          secret: seed.secret,
          digits: seed.digits,
          period: seed.period,
          algorithm: seed.algorithm,
        });
        if (!cancelled) setCode(next);
      } catch {
        if (!cancelled) setCode(i18n.t('admin_ui.authenticator.code_error'));
      }
      if (!cancelled) {
        const msLeft = totpMsUntilNext(seed.period);
        setProgress((msLeft / (seed.period * 1000)) * 100);
      }
    };

    tick();
    const interval = window.setInterval(() => {
      tick();
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [seed]);

  const copy = async () => {
    await copySecret(code.replace(/\s/g, ''));
    setCopied(true);
    if (feedbackTimer.current) window.clearTimeout(feedbackTimer.current);
    feedbackTimer.current = window.setTimeout(() => setCopied(false), 1500);
  };

  const remove = async () => {
    if (!await confirmDialog(t('admin_ui.authenticator.delete_confirm', { title: seed.title }))) return;
    setDeleting(true);
    try {
      await credentialsApi.delete(seed.id);
      onDelete();
    } catch (err) {
      void alertDialog(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setDeleting(false);
    }
  };

  const displayLabel = seed.issuer
    ? seed.issuer
    : seed.title;
  const sublabel = seed.account
    ? seed.account
    : seed.issuer && seed.title !== seed.issuer
      ? seed.title
      : null;

  // 6 cifre: "123 456"; 8 cifre: "1234 5678"; altele: fără gap
  const displayCode = useMemo(() => {
    if (code.length === 6) return `${code.slice(0, 3)} ${code.slice(3)}`;
    if (code.length === 8) return `${code.slice(0, 4)} ${code.slice(4)}`;
    return code;
  }, [code]);

  const progressColor =
    progress < 20 ? 'var(--danger)' : progress < 40 ? '#d9770680' : 'var(--accent)';

  return (
    <div
      className="card flex flex-col gap-3 relative overflow-hidden"
      style={{ minHeight: '140px' }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium truncate">{displayLabel}</div>
          {sublabel && (
            <div
              className="text-[11px] font-mono truncate mt-0.5"
              style={{ color: 'var(--text-dim)' }}
            >
              {sublabel}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={remove}
          disabled={deleting}
          title={t('common.delete')}
          className="text-[11px] hover:underline disabled:opacity-40"
          style={{ color: 'var(--text-dim)' }}
        >
          ✕
        </button>
      </div>

      <button
        type="button"
        onClick={copy}
        className="font-mono text-left tabular-nums transition-colors"
        style={{
          fontSize: '32px',
          letterSpacing: '0.08em',
          color: 'var(--text)',
          fontFeatureSettings: '"tnum"',
        }}
        title={t('admin_ui.authenticator.copy_title')}
      >
        {displayCode}
      </button>

      <div className="flex items-center justify-between text-[11px]" style={{ color: 'var(--text-dim)' }}>
        <span>{copied ? t('admin_ui.authenticator.copied') : t('admin_ui.authenticator.copy_hint')}</span>
        <span className="tabular-nums">
          {Math.ceil((progress / 100) * seed.period)}s
        </span>
      </div>

      {/* progress bar absolut jos */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px]"
        style={{ background: 'var(--border)' }}
      >
        <div
          className="h-full transition-all"
          style={{
            width: `${progress}%`,
            background: progressColor,
            transition: 'width 1s linear',
          }}
        />
      </div>
    </div>
  );
}

// ============================================================
// AddTotpCard — modal inline cu două moduri (otpauth / manual)
// ============================================================

function AddTotpCard({
  tenantId,
  ownerPublicKey,
  onClose,
  onSaved,
}: {
  tenantId: string;
  ownerPublicKey: Uint8Array;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'paste' | 'manual'>('paste');
  const [rawUrl, setRawUrl] = useState('');
  const [parsedPreview, setParsedPreview] = useState<ReturnType<typeof parseOtpauthUrl> | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const [issuer, setIssuer] = useState('');
  const [account, setAccount] = useState('');
  const [secret, setSecret] = useState('');
  const [title, setTitle] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!rawUrl.trim()) {
      setParsedPreview(null);
      setParseError(null);
      return;
    }
    try {
      const p = parseOtpauthUrl(rawUrl.trim());
      setParsedPreview(p);
      setParseError(null);
    } catch (err) {
      setParsedPreview(null);
      setParseError(err instanceof Error ? err.message : i18n.t('admin_ui.authenticator.invalid_url'));
    }
  }, [rawUrl]);

  const mutation = useMutation({
    mutationFn: async () => {
      const cfg =
        mode === 'paste' && parsedPreview
          ? parsedPreview
          : {
              issuer: issuer.trim() || null,
              account: account.trim(),
              secret: secret.replace(/\s/g, '').toUpperCase(),
              algorithm: TOTP_DEFAULTS.algorithm,
              digits: TOTP_DEFAULTS.digits,
              period: TOTP_DEFAULTS.period,
            };

      if (!cfg.secret || !isValidBase32(cfg.secret)) {
        throw new Error(t('admin_ui.totp.secret_invalid'));
      }

      const finalTitle =
        title.trim() ||
        (cfg.issuer && cfg.account
          ? `${cfg.issuer} · ${cfg.account}`
          : cfg.issuer || cfg.account || 'TOTP');

      const payload: CredentialPayload = { secret: cfg.secret };
      if (cfg.algorithm !== TOTP_DEFAULTS.algorithm) payload.algorithm = cfg.algorithm;
      if (cfg.digits !== TOTP_DEFAULTS.digits) payload.digits = cfg.digits;
      if (cfg.period !== TOTP_DEFAULTS.period) payload.period = cfg.period;

      const encrypted = await encryptCredential(payload, ownerPublicKey);
      return credentialsApi.create(tenantId, {
        type: 'totp_seed',
        title: finalTitle,
        meta: {
          issuer: cfg.issuer,
          account: cfg.account,
        },
        ciphertext: encrypted.ciphertext,
        nonce: encrypted.nonce,
        dek_wrapped: encrypted.dek_wrapped,
      });
    },
    onSuccess: () => onSaved(),
    onError: (err) => {
      setSubmitError(errorMessage(err, t('common.error')));
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    mutation.mutate();
  };

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="display-title-sm">{t('admin_ui.authenticator.add_title')}</h2>
        <button
          type="button"
          className="text-xs hover:underline"
          onClick={onClose}
          style={{ color: 'var(--text-dim)' }}
        >
          {t('common.close')}
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          type="button"
          className={`btn-outline text-xs ${mode === 'paste' ? 'ring-1 ring-[var(--accent)]' : ''}`}
          onClick={() => setMode('paste')}
        >
          {t('admin_ui.authenticator.mode_paste')}
        </button>
        <button
          type="button"
          className={`btn-outline text-xs ${mode === 'manual' ? 'ring-1 ring-[var(--accent)]' : ''}`}
          onClick={() => setMode('manual')}
        >
          {t('admin_ui.authenticator.mode_manual')}
        </button>
      </div>

      <form onSubmit={submit} className="space-y-3">
        {mode === 'paste' ? (
          <>
            <label htmlFor="authenticator-page-1" className="section-label no-dot mb-1.5">{t('admin_ui.authenticator.url_label')}</label>
            <textarea id="authenticator-page-1"
              className="input font-mono text-xs"
              rows={2}
              value={rawUrl}
              onChange={(e) => setRawUrl(e.target.value)}
              placeholder="otpauth://totp/Issuer:account@domain?secret=XXXX&issuer=..."
              autoFocus
              disabled={mutation.isPending}
            />
            {parseError && (
              <div className="text-xs" style={{ color: 'var(--danger)' }}>
                {parseError}
              </div>
            )}
            {parsedPreview && (
              <div
                className="text-xs space-y-0.5 p-2 rounded"
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                }}
              >
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>{t('security_section.issuer')}:</span>{' '}
                  <strong>{parsedPreview.issuer ?? '—'}</strong>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>{t('security_section.account')}:</span>{' '}
                  {parsedPreview.account}
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>{t('admin_ui.authenticator.algorithm_label')}:</span>{' '}
                  {t('admin_ui.authenticator.algorithm_summary', {
                    algorithm: parsedPreview.algorithm,
                    digits: parsedPreview.digits,
                    period: parsedPreview.period,
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="authenticator-page-2" className="section-label no-dot mb-1.5">{t('admin_ui.authenticator.issuer_label')}</label>
                <input id="authenticator-page-2"
                  className="input"
                  value={issuer}
                  onChange={(e) => setIssuer(e.target.value)}
                  placeholder="GitHub"
                  disabled={mutation.isPending}
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="authenticator-page-3" className="section-label no-dot mb-1.5">{t('admin_ui.authenticator.account_label')}</label>
                <input id="authenticator-page-3"
                  className="input"
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                  placeholder="me@example.com"
                  disabled={mutation.isPending}
                />
              </div>
            </div>
            <div>
              <label htmlFor="authenticator-page-4" className="section-label no-dot mb-1.5">{t('admin_ui.authenticator.secret_label')}</label>
              <input id="authenticator-page-4"
                className="input font-mono"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="JBSWY3DPEHPK3PXP"
                disabled={mutation.isPending}
              />
              {secret && !isValidBase32(secret.replace(/\s/g, '').toUpperCase()) && (
                <div className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                  {t('admin_ui.authenticator.base32_invalid')}
                </div>
              )}
            </div>
            <div className="text-xs" style={{ color: 'var(--text-dim)' }}>
              {t('admin_ui.authenticator.defaults_hint')}
            </div>
          </>
        )}

        <div>
          <label htmlFor="authenticator-page-5" className="section-label no-dot mb-1.5">{t('admin_ui.authenticator.title_label')}</label>
          <input id="authenticator-page-5"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('admin_ui.authenticator.title_placeholder')}
            disabled={mutation.isPending}
          />
        </div>

        {submitError && (
          <div
            className="text-xs rounded px-3 py-2"
            style={{
              color: 'var(--danger)',
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger)',
            }}
          >
            {submitError}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            className="btn-primary"
            disabled={
              mutation.isPending ||
              (mode === 'paste' ? !parsedPreview : !issuer.trim() || !isValidBase32(secret.replace(/\s/g, '').toUpperCase()))
            }
          >
            {mutation.isPending ? t('admin_ui.authenticator.encrypting') : t('common.save')}
          </button>
          <button
            type="button"
            className="btn-outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function metaString(s: CredentialSummary, key: string): string | null {
  const v = s.meta?.[key];
  return typeof v === 'string' && v.trim() ? v : null;
}

function numOr(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = parseInt(v, 10);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}
